import React from 'react';
import type { BreadcrumbList, WithContext } from 'schema-dts';
import { withTrailingSlash } from 'helpers/url/withTrailingSlash';
import { classnames } from 'helpers/utils/classnames';

export interface Crumb {
  label: string;
  href?: string;
}

// Absolute base for the BreadcrumbList `item` URLs. Google wants absolute URLs here, and this
// component also renders inside client components where the request origin is not available.
export const SHOP_ORIGIN = 'https://shop.viewrail.com';

// The BreadcrumbList JSON-LD for a trail, built from the same crumbs the user sees so the schema
// follows the shop's navigation flow (Home / Cable Railing / …) rather than the catalog hierarchy.
// The current page's crumb has no link, and schema.org allows omitting `item` on the last element.
export const breadcrumbListJsonLd = (crumbs: Crumb[], origin = SHOP_ORIGIN): WithContext<BreadcrumbList> => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: crumbs.map((crumb, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: crumb.label,
    ...(crumb.href && i < crumbs.length - 1 && { item: toAbsolute(crumb.href, origin) }),
  })),
});

const toAbsolute = (href: string, origin: string) =>
  href.startsWith('/') ? `${origin}${withTrailingSlash(href)}` : href;

// JSON.stringify does not escape `<`, so a catalog label containing `</script>` would close the
// JSON-LD element and let the rest of the label run as script. Escaping `<` as \u003c keeps the
// JSON valid and makes the string inert inside a <script> element (same treatment for `>` and `&`).
export const serializeJsonLd = (data: unknown): string =>
  JSON.stringify(data).replace(/</g, '\\u003c').replace(/>/g, '\\u003e').replace(/&/g, '\\u0026');

// Brand-refresh breadcrumb: GT Standard, gray links with a de-linked current page. Shared by the
// PLP and PDP so the trail is styled and structured identically. The trail stays on one line and
// scrolls sideways (scrollbar hidden) instead of wrapping under long product names. Emits the
// matching BreadcrumbList JSON-LD alongside, so the schema can never drift from the visible trail.
const Breadcrumb = ({ crumbs, className }: { crumbs: Crumb[]; className?: string }) => {
  if (!crumbs.length) return null;

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: serializeJsonLd(breadcrumbListJsonLd(crumbs)) }} />
      <nav
        aria-label="Breadcrumb"
        className={classnames(
          'flex items-center gap-x-6 overflow-x-auto whitespace-nowrap font-gt text-14 leading-[1.2] text-[#5e5e5e] [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
          className,
        )}
      >
        {crumbs.map((crumb, i) => {
          const isLast = i === crumbs.length - 1;
          return (
            <React.Fragment key={`${crumb.label}-${i}`}>
              {i > 0 && <span aria-hidden>/</span>}
              {isLast || !crumb.href ? (
                <span aria-current={isLast ? 'page' : undefined} className="shrink-0 py-8 text-[#212121]">
                  {crumb.label}
                </span>
              ) : (
                <a href={withTrailingSlash(crumb.href)} className="shrink-0 py-8 hover:underline">
                  {crumb.label}
                </a>
              )}
            </React.Fragment>
          );
        })}
      </nav>
    </>
  );
};

export default Breadcrumb;
